/**
 * Extract dominant colors from an image using canvas pixel sampling.
 * Returns an object with primary and secondary hex colors.
 */

const SAMPLE_SIZE = 10; // grid divisions per axis
const MIN_SATURATION = 30; // skip near-gray pixels
const MIN_BRIGHTNESS = 30;
const MAX_BRIGHTNESS = 220;

function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
}

function rgbToHex(r, g, b) {
  return '#' + [r, g, b].map(c => c.toString(16).padStart(2, '0')).join('');
}

function colorDistance(c1, c2) {
  return Math.sqrt(
    Math.pow(c1[0] - c2[0], 2) +
    Math.pow(c1[1] - c2[1], 2) +
    Math.pow(c1[2] - c2[2], 2)
  );
}

function clusterColors(pixels, k = 6, iterations = 10) {
  if (pixels.length === 0) return [];
  if (pixels.length <= k) return pixels;

  // Initialize centroids using spread across the sorted pixel list
  let centroids = [];
  const step = Math.floor(pixels.length / k);
  for (let i = 0; i < k; i++) {
    centroids.push([...pixels[Math.min(i * step, pixels.length - 1)]]);
  }

  for (let iter = 0; iter < iterations; iter++) {
    const clusters = Array.from({ length: k }, () => []);

    for (const pixel of pixels) {
      let minDist = Infinity;
      let closest = 0;
      for (let i = 0; i < centroids.length; i++) {
        const dist = colorDistance(pixel, centroids[i]);
        if (dist < minDist) {
          minDist = dist;
          closest = i;
        }
      }
      clusters[closest].push(pixel);
    }

    // Update centroids
    for (let i = 0; i < k; i++) {
      if (clusters[i].length === 0) continue;
      centroids[i] = [
        Math.round(clusters[i].reduce((s, p) => s + p[0], 0) / clusters[i].length),
        Math.round(clusters[i].reduce((s, p) => s + p[1], 0) / clusters[i].length),
        Math.round(clusters[i].reduce((s, p) => s + p[2], 0) / clusters[i].length),
      ];
    }
  }

  return centroids;
}

/**
 * Extract primary and secondary colors from an image URL.
 * @param {string} imageUrl - The image URL to analyze
 * @returns {Promise<{ primary: string, secondary: string }>}
 */
export function extractColors(imageUrl) {
  return new Promise((resolve) => {
    const fallback = { primary: '#FF7722', secondary: '#8E827C' };

    if (!imageUrl) {
      resolve(fallback);
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.referrerPolicy = 'no-referrer';

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        const w = Math.min(img.naturalWidth, 100);
        const h = Math.min(img.naturalHeight, 100);
        canvas.width = w;
        canvas.height = h;

        ctx.drawImage(img, 0, 0, w, h);
        const imageData = ctx.getImageData(0, 0, w, h);
        const data = imageData.data;

        const pixels = [];
        const cellW = Math.floor(w / SAMPLE_SIZE);
        const cellH = Math.floor(h / SAMPLE_SIZE);

        // Sample pixels from grid centers
        for (let gy = 0; gy < SAMPLE_SIZE; gy++) {
          for (let gx = 0; gx < SAMPLE_SIZE; gx++) {
            const cx = Math.floor((gx + 0.5) * cellW);
            const cy = Math.floor((gy + 0.5) * cellH);

            // Sample a small cluster around center
            for (let dy = -1; dy <= 1; dy++) {
              for (let dx = -1; dx <= 1; dx++) {
                const px = Math.min(Math.max(cx + dx, 0), w - 1);
                const py = Math.min(Math.max(cy + dy, 0), h - 1);
                const idx = (py * w + px) * 4;
                const r = data[idx];
                const g = data[idx + 1];
                const b = data[idx + 2];

                const hsl = rgbToHsl(r, g, b);

                // Filter out near-gray, too dark, or too bright pixels
                if (hsl.s >= MIN_SATURATION && hsl.l >= MIN_BRIGHTNESS && hsl.l <= MAX_BRIGHTNESS) {
                  pixels.push([r, g, b]);
                }
              }
            }
          }
        }

        if (pixels.length < 2) {
          resolve(fallback);
          return;
        }

        const clusters = clusterColors(pixels, 6);

        // Score clusters: prefer saturated, medium-brightness colors
        const scored = clusters.map(c => {
          const hsl = rgbToHsl(c[0], c[1], c[2]);
          // Score by saturation and how far from pure gray
          const saturationScore = hsl.s;
          // Slight preference for warm colors (hue 0-60, 300-360)
          const warmBonus = (hsl.h <= 60 || hsl.h >= 300) ? 10 : 0;
          return { rgb: c, score: saturationScore + warmBonus, hsl };
        });

        scored.sort((a, b) => b.score - a.score);

        const primary = scored[0];
        const secondary = scored[1] || scored[0];

        // Ensure secondary is sufficiently different from primary
        let finalSecondary = secondary;
        if (finalSecondary && colorDistance(primary.rgb, finalSecondary.rgb) < 40) {
          // Find the most different color
          let maxDist = 0;
          for (const s of scored.slice(1)) {
            const dist = colorDistance(primary.rgb, s.rgb);
            if (dist > maxDist) {
              maxDist = dist;
              finalSecondary = s;
            }
          }
        }

        resolve({
          primary: rgbToHex(...primary.rgb),
          secondary: rgbToHex(...finalSecondary.rgb),
        });
      } catch {
        resolve(fallback);
      }
    };

    img.onerror = (e) => {
      console.warn('Color extraction image load error:', e);
      resolve(fallback);
    };
    // Append a unique query parameter to bypass browser caching without CORS headers
    const bypassUrl = imageUrl + (imageUrl.includes('?') ? '&' : '?') + 'not-from-cache-please';
    img.src = bypassUrl;
  });
}
